import React from 'react';
import { Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ReportVisual } from '../../services/reportsApi';
import ReportEmptyState from './ReportEmptyState';

type ReportTopNBarProps = {
  visual: ReportVisual;
  compact?: boolean;
  onOpenLink?: (href?: string) => void;
};

function renderOpenLink(href: string, onOpenLink?: (href?: string) => void) {
  if (onOpenLink) {
    return (
      <Button size="small" variant="text" endIcon={<OpenInNewIcon />} onClick={() => onOpenLink(href)}>
        Open
      </Button>
    );
  }
  return (
    <Button size="small" variant="text" endIcon={<OpenInNewIcon />} href={href}>
      Open
    </Button>
  );
}

const ReportTopNBar: React.FC<ReportTopNBarProps> = ({ visual, compact = false, onOpenLink }) => {
  const items = Array.isArray(visual.data?.items) ? visual.data.items : [];
  const maxValue = Math.max(0, ...items.map((item: Record<string, unknown>) => Number(item?.value || 0)));
  const isFallbackRenderer = visual.type === 'stacked_bar' || visual.type === 'trend_line';

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, height: '100%' }}>
      <Typography variant="h6">{visual.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1 : 1.5 }}>
        {visual.insight}
      </Typography>
      {isFallbackRenderer ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: compact ? 1 : 1.5 }}>
          Compact fallback rendering for {visual.type.replace('_', ' ')}.
        </Typography>
      ) : null}

      {visual.empty_state ? (
        <ReportEmptyState
          title={visual.empty_state.title}
          detail={visual.empty_state.detail}
          missingData={visual.empty_state.missing_data}
          compact={compact}
        />
      ) : (
        <Stack spacing={compact ? 1 : 1.25}>
          {items.map((item: Record<string, unknown>, index: number) => {
            const value = Number(item?.value || 0);
            const widthPct = maxValue > 0 ? Math.max(8, (value / maxValue) * 100) : 0;
            const href = typeof item?.href === 'string' ? item.href : '';
            return (
              <Box key={String(item?.id || index)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
                  <Box sx={{ minWidth: 0 }}>
                    {href ? (
                      <Link href={href} underline="hover" color="inherit">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {String(item?.label || 'Item')}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {String(item?.label || 'Item')}
                      </Typography>
                    )}
                    {item?.secondary_label || item?.detail ? (
                      <Typography variant="caption" color="text.secondary">
                        {String(item?.secondary_label || item?.detail || '')}
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {value}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1, height: 12, bgcolor: 'grey.100', borderRadius: 999 }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${widthPct}%`,
                        borderRadius: 999,
                        bgcolor: 'secondary.main',
                      }}
                    />
                  </Box>
                  {item?.status ? (
                    <Typography variant="caption" color="text.secondary">
                      {String(item.status)}
                    </Typography>
                  ) : null}
                  {href ? renderOpenLink(href, onOpenLink) : null}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default ReportTopNBar;
